/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import PharmacyBranchTransferInReports from "./transferInReports";
import PharmacyBranchTransferOutReports from "./transferOutReports";
import moment from "moment";
import PharmacyBranchTransferInAndOutReports from "./totalTransferInAndOutReports";
import TransferReportsTable from "../TransferReportsTable";
import uvClaimsService from "../../services/uv-claims.service";
import * as XLSX from "xlsx";

const NUMERIC_TOTAL_KEYS = [
  "Patient Pays",
  "Total Paid",
  "Dispensing Fee",
  "Ingredient Cost",
  "Usual & Customary",
  "Gross Amount Due",
];

const parseNum = (v) => {
  if (v == null || v === "") return 0;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const formatCurrency = (n) =>
  Number.isFinite(n) ? n.toFixed(2) : "0.00";

const PharmacyBranchReports = ({ pharmacyBranchId, pharmacyBranchName }) => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showUVModal, setShowUVModal] = useState(false);
  const [uvStartDate, setUVStartDate] = useState("");
  const [uvEndDate, setUVEndDate] = useState("");
  const [uvClaims, setUVClaims] = useState([]);
  const [uvLoading, setUVLoading] = useState(false);
  const [uvError, setUVError] = useState(null);
  const [uvFetchedOnce, setUVFetchedOnce] = useState(false);
  const [exportColumns, setExportColumns] = useState(new Set());

  const fetchUVReport = async () => {
    if (!uvStartDate || !uvEndDate) return;
    setUVLoading(true);
    setUVError(null);
    try {
      const data = await uvClaimsService.getClaimsReport(uvStartDate, uvEndDate);
      setUVClaims(Array.isArray(data) ? data : []);
    } catch (err) {
      setUVError(err?.message || err?.response?.data?.message || "Failed to load UV claims.");
    } finally {
      setUVLoading(false);
      setUVFetchedOnce(true);
    }
  };

  const uvTotals = React.useMemo(() => {
    const totals = { count: uvClaims.length };
    NUMERIC_TOTAL_KEYS.forEach((key) => {
      totals[key] = uvClaims.reduce((sum, row) => sum + parseNum(row[key]), 0);
    });
    return totals;
  }, [uvClaims]);

  const uvColumns = React.useMemo(() => {
    if (uvClaims.length === 0) return [];
    return Object.keys(uvClaims[0]);
  }, [uvClaims]);

  useEffect(() => {
    if (uvColumns.length > 0) {
      setExportColumns(new Set(uvColumns));
    } else {
      setExportColumns(new Set());
    }
  }, [uvColumns.join(",")]);

  const toggleExportColumn = (col) => {
    setExportColumns((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  };

  const selectAllExportColumns = () => setExportColumns(new Set(uvColumns));
  const deselectAllExportColumns = () => setExportColumns(new Set());

  const exportToExcel = () => {
    const cols = uvColumns.filter((c) => exportColumns.has(c));
    if (cols.length === 0 || uvClaims.length === 0) return;
    const rows = uvClaims.map((row) => cols.map((col) => (row[col] != null ? row[col] : "")));
    const ws = XLSX.utils.aoa_to_sheet([cols, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "UV Report");
    XLSX.writeFile(
      wb,
      `reports-from-uv-${uvStartDate}-to-${uvEndDate}-${moment().format("YYYY-MM-DD")}.xlsx`
    );
  };

  const showReportsFromUV = pharmacyBranchId === "82844";

  return (
    <>
      <h2>{pharmacyBranchName} Reports:</h2>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
        <input
          id="date-of-birth-input"
          test-id="date-of-birth-input"
          name="fromDate"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          type="date"
          max={moment().format("yyyy-MM-DD")}
          className="form-control"
          style={{ width: 250 }}
        />
        <input
          id="date-of-birth-input"
          test-id="date-of-birth-input"
          name="toDate"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          type="date"
          max={moment().format("yyyy-MM-DD")}
          className="form-control"
          style={{ width: 250 }}
        />
        {showReportsFromUV && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowUVModal(true)}
          >
            Reports from UV
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <PharmacyBranchTransferInReports pharmacyBranchId={pharmacyBranchId} fromDate={fromDate} toDate={toDate} />
        <PharmacyBranchTransferOutReports pharmacyBranchId={pharmacyBranchId} fromDate={fromDate} toDate={toDate} />
        <PharmacyBranchTransferInAndOutReports pharmacyBranchId={pharmacyBranchId} fromDate={fromDate} toDate={toDate} />
      </div>

      {fromDate && toDate && (
        <div className="mt-4">
          <TransferReportsTable
            pharmacyBranchId={pharmacyBranchId}
            pharmacyBranchName={pharmacyBranchName}
            fromDate={fromDate}
            toDate={toDate}
          />
        </div>
      )}

      <Modal
        show={showUVModal}
        onHide={() => setShowUVModal(false)}
        aria-labelledby="reports-from-uv-modal-title"
        centered
        backdrop="static"
        scrollable
        fullscreen="lg-down"
        dialogClassName="modal-xl"
      >
        <Modal.Header closeButton>
          <Modal.Title id="reports-from-uv-modal-title">Reports from UV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label">Start date</label>
              <input
                type="date"
                className="form-control"
                value={uvStartDate}
                max={moment().format("YYYY-MM-DD")}
                onChange={(e) => setUVStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">End date</label>
              <input
                type="date"
                className="form-control"
                value={uvEndDate}
                max={moment().format("YYYY-MM-DD")}
                onChange={(e) => setUVEndDate(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <button
                type="button"
                className="btn btn-primary"
                onClick={fetchUVReport}
                disabled={uvLoading || !uvStartDate || !uvEndDate}
              >
                {uvLoading ? "Loading…" : "Get report"}
              </button>
            </div>
          </div>

          {uvError && (
            <div className="alert alert-danger" role="alert">
              {uvError}
            </div>
          )}

          {uvLoading && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" />
              <p className="mt-2 mb-0 text-muted">Loading UV claims…</p>
            </div>
          )}

          {!uvLoading && uvClaims.length > 0 && (
            <>
              <div className="card mb-3">
                <div className="card-header fw-bold">Totals</div>
                <div className="card-body">
                  <p className="mb-2">
                    <strong>Total claims:</strong> {uvTotals.count}
                  </p>
                  <div className="row g-2">
                    {NUMERIC_TOTAL_KEYS.filter((k) => uvColumns.includes(k)).map((key) => (
                      <div key={key} className="col-md-4 col-lg-2">
                        <strong>{key}:</strong> {formatCurrency(uvTotals[key])}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card mb-3">
                <div className="card-header fw-bold d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>Export to Excel</span>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={selectAllExportColumns}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={deselectAllExportColumns}
                    >
                      Deselect all
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={exportToExcel}
                      disabled={exportColumns.size === 0}
                    >
                      Export to Excel
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <p className="text-muted small mb-2">Columns to include in export (all selected by default):</p>
                  <div className="row g-2" style={{ maxHeight: 200, overflowY: "auto" }}>
                    {uvColumns.map((col) => (
                      <div key={col} className="col-6 col-md-4 col-lg-3">
                        <label className="d-flex align-items-center gap-2 small cursor-pointer">
                          <input
                            type="checkbox"
                            checked={exportColumns.has(col)}
                            onChange={() => toggleExportColumn(col)}
                          />
                          <span className="text-truncate" title={col}>{col}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          {uvColumns.map((col) => (
                            <th key={col}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {uvClaims.map((row, index) => (
                          <tr key={row["Claim ID"] || index}>
                            {uvColumns.map((col) => (
                              <td key={col}>{row[col] != null ? String(row[col]) : ""}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {!uvLoading && uvFetchedOnce && uvClaims.length === 0 && !uvError && (
            <p className="text-muted">No claims returned for this date range.</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PharmacyBranchReports;
